import { z } from 'zod'
import {
  ChartSpecSchema,
  type ChartSpec,
} from '@/features/deep-research/schemas/deep-research-types'

/**
 * Visuals inside a chat answer (2026-10-02): the AI writes a fenced block and
 * the chat draws it in place of code. It travels in the message text, so it
 * survives reloads and synced threads.
 *
 * - ```chart        a data chart, the Deep Research ChartSpec (bar, line, ...)
 * - ```price-chart  a candlestick chart for a ticker, pinned to a date
 */
export const CHAT_VISUAL_LANGS: ReadonlySet<string> = new Set(['chart', 'price-chart'])

/** Timeframes the in-chat price chart offers (its timeframe chips). */
export const PRICE_CHART_INTERVALS = ['1D', '15m', '5m', '1m'] as const

export const PriceChartSpecSchema = z.object({
  ticker: z.string().regex(/^[A-Za-z.]{1,10}$/),
  /** YYYY-MM-DD the chart ends on (a past session); omitted = today. */
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  interval: z.enum(PRICE_CHART_INTERVALS).optional(),
  title: z.string().max(120).optional(),
})
export type PriceChartSpec = z.infer<typeof PriceChartSpecSchema>

/** Most points a data chart may carry; beyond this it is a table, not a chart. */
export const MAX_CHART_POINTS = 200

export type ChatVisual = { kind: 'data'; spec: ChartSpec } | { kind: 'price'; spec: PriceChartSpec }

/** The visual a fenced block describes, or null when it is not one (or is malformed). */
export function parseChatVisual(lang: string, code: string): ChatVisual | null {
  if (!CHAT_VISUAL_LANGS.has(lang)) return null
  let raw: unknown
  try {
    raw = JSON.parse(code)
  } catch {
    return null
  }
  if (lang === 'price-chart') {
    const parsed = PriceChartSpecSchema.safeParse(raw)
    return parsed.success
      ? { kind: 'price', spec: { ...parsed.data, ticker: parsed.data.ticker.toUpperCase() } }
      : null
  }
  const parsed = ChartSpecSchema.safeParse(raw)
  if (!parsed.success) return null
  const spec = parsed.data
  if (spec.data.length === 0 || spec.data.length > MAX_CHART_POINTS || !spec.y_fields.length) {
    return null
  }
  return { kind: 'data', spec }
}
